import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Search, Bell, LogOut, User, Settings, Menu, X,
  Check, Clock, Sun, Moon, CreditCard, HelpCircle,
  ExternalLink, BookOpen, Target, FileText, Mail,
  Shield, Download, BarChart3, CheckCircle2, Rocket,
  PlusCircle, Trash2, Lock, Unlock, RefreshCw,
  AlertTriangle, Zap, Brain, DollarSign
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import { toast } from 'sonner';
import useNotifications from '../hooks/useNotifications';

// Relative time formatter
function formatTimeAgo(timeStr) {
  if (!timeStr) return '';
  try {
    const then = new Date(timeStr);
    const now = new Date();
    const diff = Math.floor((now - then) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
    if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
    if (diff < 604800) return Math.floor(diff / 86400) + 'd ago';
    return then.toLocaleDateString();
  } catch { return timeStr; }
}

export default function TopHeader({ onToggleSidebar, isSidebarOpen }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const userId = user?.id || localStorage.getItem('user_id') || 'anonymous';
  const { pending2FA } = useAuth();
  const isAdmin = user?.is_admin || user?.role === 'admin' || user?.role === 'super_admin';
  const showAdmin = isAdmin && !pending2FA;

  // ── COMING SOON STATUS (WebSocket-powered) ─────────────────────
  const [comingSoonEnabled, setComingSoonEnabled] = useState(false);
  
  useEffect(() => {
    if (!isAdmin) return;
    
    // Initial fetch
    const checkComingSoon = async () => {
      try {
        const res = await api.get('/api/admin/operations/check/coming_soon');
        setComingSoonEnabled(res.data?.enabled || false);
      } catch {}
    };
    checkComingSoon();
    
    // Listen for WebSocket messages (from App.js WebSocket or direct connection)
    const handleSystemMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'system_setting_changed' && data.setting === 'coming_soon') {
          setComingSoonEnabled(data.value);
        }
      } catch {}
    };
    
    // Hook into existing WebSocket if available, or create a dedicated one
    const userId = user?.id || localStorage.getItem('user_id');
    if (userId) {
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const wsHost = isLocal ? 'localhost:8000' : 'api.ileadx.io';
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const wsUrl = `${protocol}://${wsHost}/api/notifications/ws/${userId}?is_admin=true`;
      
      let ws;
      let reconnectTimer;
      
      const connect = () => {
        try {
          ws = new WebSocket(wsUrl);
          
          ws.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);
              if (data.type === 'system_setting_changed') {
                setComingSoonEnabled(data.setting === 'coming_soon' ? data.value : comingSoonEnabled);
              }
            } catch {}
          };
          
          ws.onclose = () => {
            reconnectTimer = setTimeout(connect, 5000);
          };
          
          ws.onerror = () => {
            ws.close();
          };
        } catch {}
      };
      
      connect();
      
      return () => {
        clearTimeout(reconnectTimer);
        if (ws) ws.close();
      };
    }
  }, [isAdmin, user?.id]);

  const { notifications, unreadCount, markRead, markAllRead } = useNotifications(userId, isAdmin);
  
  const searchRef = useRef(null);
  const notifRef = useRef(null);
  const profileRef = useRef(null);
  const searchInputRef = useRef(null);
  const helpRef = useRef(null);

  const currentPlan = user?.plan || 'free';
  const planLabel = currentPlan === 'free' ? 'Free' : currentPlan === 'growth' ? 'Growth' : currentPlan === 'pro' ? 'Pro' : 'Enterprise';
  const planInitial = planLabel.charAt(0);


  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearch(false);
        if (isMobile) setShowMobileSearch(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifications(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
      if (helpRef.current && !helpRef.current.contains(e.target)) setShowHelpModal(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isMobile]);

  const handleSearch = async (value) => {
    setSearchQuery(value);
    if (value.length < 2) { setSearchResults([]); setShowSearch(false); return; }
    setSearchLoading(true);
    try {
      const res = await api.get('/api/leads/history', { params: { search: value, limit: 8 } });
      setSearchResults(res.data.leads || []);
      setShowSearch(true);
    } catch { setSearchResults([]); }
    finally { setSearchLoading(false); }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userInitials = (user?.full_name || user?.email || 'U')
    .split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <>
      <header style={{
        height: 87, background: 'var(--header-bg)',
        borderBottom: '1px solid var(--border-primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 var(--space-md)', position: 'sticky', top: 0, zIndex: 500,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        width: '100%', maxWidth: '100vw', boxSizing: 'border-box', gap: 'var(--space-sm)',
      }}>
        
        {/* ═══════════ LEFT SECTION ═══════════ */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexShrink: 0 }}>
          {/* Mobile hamburger — large for easy tap */}
          <button onClick={onToggleSidebar} aria-label="Toggle menu"
            style={{
              ...iconBtnStyle,
              width: 52, height: 52, borderRadius: 'var(--radius-xl)',
              background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)',
              display: 'none',  // Hidden by default (desktop)
            }}
            className="hamburger-btn">
            <Menu size={28} />
          </button>
          {/* iLeadX logo — visible only on mobile */}
          <a href="/dashboard" className="mobile-logo" style={{
            display: 'none',
            fontSize: 18, fontWeight: 800, color: 'var(--text-primary)',
            letterSpacing: '-0.5px', marginLeft: 4,
            textDecoration: 'none', cursor: 'pointer',
          }}>
            <img src="/iLeadX-logo.png" alt="iLeadX" style={{ height: 28, width: 'auto' }} />
          </a>
          <style>{`
            @media (max-width: 768px) {
              .hamburger-btn { display: flex !important; }
              .mobile-logo { display: inline-flex !important; align-items: center; }
            }
          `}</style>
        </div>

        {/* ═══════════ SEARCH ═══════════ */}
        <div ref={searchRef} style={{
          position: 'relative', flex: 1, maxWidth: 400, minWidth: 0,
          display: isMobile ? (showMobileSearch ? 'block' : 'none') : 'block',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-xs)',
            background: 'var(--input-bg)', borderRadius: 'var(--radius-md)',
            padding: '0 var(--space-sm)', height: 36,
            border: '1px solid var(--input-border)',
          }}>
            <Search size={18} color="var(--text-secondary)" />
            <input ref={searchInputRef} type="text" value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => { if (searchResults.length > 0) setShowSearch(true); }}
              placeholder="Search leads, industries, locations..."
              style={{ flex: 1, background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: 'var(--text-sm)', outline: 'none', minWidth: 0, fontFamily: 'inherit' }}
            />
          </div>

          {/* Search Dropdown */}
          {showSearch && (searchResults.length > 0 || searchLoading) && (
            <div style={{ position: 'absolute', top: 44, left: 0, right: 0, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-primary)', boxShadow: 'var(--shadow-lg)', zIndex: 100, overflow: 'hidden', maxHeight: 360, overflowY: 'auto' }}>
              {searchLoading ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
                  <span style={{ width: 20, height: 20, border: '2px solid var(--border-primary)', borderTop: '2px solid var(--accent-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                  <p style={{ color: 'var(--text-tertiary)', fontSize: 11, marginTop: 8 }}>Searching...</p>
                </div>
              ) : (
                searchResults.map((lead, i) => (
                  <div key={i} onClick={() => { setShowSearch(false); setSearchQuery(''); setShowMobileSearch(false); navigate(`/dashboard/company/${encodeURIComponent(lead.company_name)}`); }}
                    style={{ padding: 'var(--space-md)', cursor: 'pointer', borderBottom: i < searchResults.length-1 ? '1px solid var(--border-primary)' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.company_name}</div>
                      <div style={{ color: 'var(--text-tertiary)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[lead.industry, lead.city, lead.country].filter(Boolean).join(' · ')}</div>
                    </div>
                    {lead.lead_score && (
                      <span style={{ padding: '2px 8px', borderRadius: 'var(--radius-full)', fontSize: 10, fontWeight: 600, flexShrink: 0, marginLeft: 8, background: lead.lead_score >= 70 ? 'var(--success-bg)' : 'var(--warning-bg)', color: lead.lead_score >= 70 ? 'var(--success)' : 'var(--warning)' }}>{lead.lead_score}</span>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* ═══════════ RIGHT SECTION ═══════════ */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', flexShrink: 0 }}>
          
          {/* Mobile search toggle */}
          <button onClick={() => { setShowMobileSearch(!showMobileSearch); if (!showMobileSearch) setTimeout(() => searchInputRef.current?.focus(), 100); }}
            className="mobile-only" style={iconBtnStyle}>
            {showMobileSearch ? <X size={22} /> : <Search size={22} />}
          </button>

          {/* Theme toggle */}
          <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            style={iconBtnStyle}>
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          {/* ── COMING SOON INDICATOR (Admin only, WebSocket-powered) ── */}
          {isAdmin && comingSoonEnabled && (
            <button
              onClick={() => navigate('/dashboard/admin/operations')}
              title="Coming Soon Mode is ACTIVE,  click to manage"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '4px 14px',
                borderRadius: 20,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <span style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: '#F59E0B',
                display: 'inline-block',
                flexShrink: 0,
                animation: 'comingSoonPulse 2s ease-in-out infinite',
                boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.6)',
              }} />
              <span style={{
                color: '#F59E0B',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.03em',
                whiteSpace: 'nowrap',
              }}>
                Coming Soon
              </span>
            </button>
          )}

          {/* Notifications */}
          <div ref={notifRef} style={{ position: 'relative' }}>
            <button onClick={() => { setShowNotifications(prev => !prev); setShowProfile(false); }}
              aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
              style={iconBtnStyle}>
              <Bell size={20} />
              {unreadCount > 0 && (
                <span style={{ position: 'absolute', top: 4, right: 4, width: 16, height: 16, borderRadius: '50%', background: 'var(--error)', color: 'white', fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, animation: 'notificationBounce 0.4s ease' }}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div style={{ position: 'absolute', top: 44, right: 0, width: 340, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-primary)', boxShadow: 'var(--shadow-lg)', zIndex: 100, overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-sm) var(--space-md)', borderBottom: '1px solid var(--border-primary)' }}>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 13 }}>Notifications</span>
                  {unreadCount > 0 && (
                    <button onClick={markAllRead} style={{ background: 'none', border: 'none', color: 'var(--success)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Mark all read</button>
                  )}
                </div>
                <div style={{ maxHeight: '60vh', overflowY: 'auto', minWidth: 380 }}>
                  {notifications.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 'var(--space-xl)', color: 'var(--text-secondary)', fontSize: 13 }}>
                      <Bell size={32} style={{ display: 'block', margin: '0 auto 10px', opacity: 0.15 }} />
                      <p style={{ margin: 0 }}>No notifications yet</p>
                    </div>
                  ) : (
                    notifications.map(n => (
                      <div key={n.id} style={{ padding: 'var(--space-sm) var(--space-md)', cursor: 'pointer', borderBottom: '1px solid var(--border-primary)', background: n.read ? 'transparent' : 'var(--accent-glow)', display: 'flex', gap: 'var(--space-sm)' }}
                        onClick={() => markRead(n.id)}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                        onMouseLeave={e => e.currentTarget.style.background = n.read ? 'transparent' : 'var(--accent-glow)'}>
                        <span style={{ flexShrink: 0, marginTop: 1 }}>
                          {n.icon === 'Bell' ? <Bell size={18} /> :
                           n.icon === 'CheckCircle2' ? <CheckCircle2 size={18} color="var(--success)" /> :
                           n.icon === 'Download' ? <Download size={18} color="var(--info)" /> :
                           n.icon === 'BarChart3' ? <BarChart3 size={18} color="var(--info)" /> :
                           n.icon === 'Rocket' ? <Rocket size={18} color="var(--accent-primary)" /> :
                           n.icon === 'PlusCircle' ? <PlusCircle size={18} color="var(--accent-primary)" /> :
                           n.icon === 'Trash2' ? <Trash2 size={18} color="var(--error)" /> :
                           n.icon === 'Lock' ? <Lock size={18} color="var(--warning)" /> :
                           n.icon === 'Unlock' ? <Unlock size={18} color="var(--info)" /> :
                           n.icon === 'XCircle' ? <X size={18} color="var(--error)" /> :
                           n.icon === 'RefreshCw' ? <RefreshCw size={18} color="var(--info)" /> :
                           n.icon === 'User' ? <User size={18} /> :
                           n.icon === 'Search' ? <Search size={18} color="var(--accent-primary)" /> :
                           n.icon === 'Mail' ? <Mail size={18} color="var(--info)" /> :
                           n.icon === 'AlertTriangle' ? <AlertTriangle size={18} color="var(--warning)" /> :
                           n.icon === 'Zap' ? <Zap size={18} color="var(--warning)" /> :
                           n.icon === 'Brain' ? <Brain size={18} color="var(--accent-secondary)" /> :
                           n.icon === 'DollarSign' ? <DollarSign size={18} color="var(--error)" /> :
                           n.icon === 'FileText' ? <FileText size={18} color="var(--info)" /> :
                           n.icon === 'Check' ? <Check size={18} color="var(--success)" /> :
                           <Bell size={18} />}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: 'var(--text-primary)', fontSize: 12, fontWeight: 500 }}>{n.title}</div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: 11, marginTop: 1 }}>{n.description || n.desc || ''}</div>
                          <div style={{ color: 'var(--text-tertiary)', fontSize: 10, marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={10} /> {formatTimeAgo(n.time)}</div>
                        </div>
                        {!n.read && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent-primary)', flexShrink: 0, marginTop: 4 }} />}
                      </div>
                    ))
                  )}
                </div>
                {notifications.length > 0 && (
                  <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border-primary)', textAlign: 'center' }}>
                    <a href="/dashboard/notifications" style={{ color: 'var(--accent-primary)', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                      View all notifications →
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Profile */}
          <div ref={profileRef} style={{ position: 'relative' }}>
            <button onClick={() => { setShowProfile(prev => !prev); setShowNotifications(false); }}
              aria-label="User menu" style={{
                width: 42, height: 42, borderRadius: 'var(--radius-lg)', border: 'none',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, flexShrink: 0, padding: 0,
                background: user?.avatar_url ? 'transparent' : 'var(--accent-gradient)',
                color: user?.avatar_url ? 'transparent' : 'white',
                overflow: 'hidden',
              }}>
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" referrerPolicy="no-referrer" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                userInitials
              )}
            </button>

            {showProfile && (
              <div style={{ position: 'absolute', top: 42, right: 0, width: 260, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-primary)', boxShadow: 'var(--shadow-lg)', zIndex: 100, overflow: 'hidden' }}>
                <div style={{ padding: 'var(--space-md)', borderBottom: '1px solid var(--border-primary)', textAlign: 'center' }}>
                  <div style={{ width: 52, height: 52, borderRadius: 'var(--radius-xl)', margin: '0 auto var(--space-sm)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {user?.avatar_url ? (
                      <img src={user.avatar_url} alt="" referrerPolicy="no-referrer" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', background: 'var(--accent-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 15 }}>
                        {userInitials}
                      </div>
                    )}
                  </div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 13 }}>{user?.full_name || 'User'}</div>
                  <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>{user?.email}</div>
                </div>
                <DropdownItem icon={<Settings size={18} />} label="Settings" onClick={() => { setShowProfile(false); navigate('/dashboard/settings'); }} />

                <DropdownItem icon={<HelpCircle size={18} />} label="Help & Support" onClick={() => { setShowProfile(false); setShowHelpModal(true); }} />
                <DropdownItem 
                  icon={<CreditCard size={18} />} 
                  label={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      Billing & Usage
                      <span style={{
                        background: currentPlan === 'free' ? 'transparent' : 'var(--accent-glow)',
                        color: currentPlan === 'free' ? 'var(--text-tertiary)' : 'var(--accent-primary)',
                        border: `1px solid ${currentPlan === 'free' ? 'var(--border-primary)' : 'var(--accent-primary)'}`,
                        padding: '1px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                        textTransform: 'capitalize',
                      }}>
                        {currentPlan}
                      </span>
                    </span>
                  } 
                  onClick={() => { setShowProfile(false); navigate('/dashboard/billing'); }} 
                />
                <div style={{ borderTop: '1px solid var(--border-primary)' }}>
                  <DropdownItem icon={<LogOut size={18} />} label="Logout" onClick={() => { setShowProfile(false); logout(); window.location.href = '/login'; }} danger />
                </div>
                {notifications.length > 0 && (
                  <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border-primary)', textAlign: 'center' }}>
                    <a href="/dashboard/notifications" style={{ color: 'var(--accent-primary)', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                      View all notifications →
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ═══════════ HELP & SUPPORT MODAL ═══════════ */}
      {showHelpModal && (
        <div ref={helpRef} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
        }} onClick={() => setShowHelpModal(false)}>
          <div style={{
            background: 'var(--bg-secondary)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-2xl)',
            maxWidth: 440, width: '100%', border: '1px solid var(--border-primary)',
            boxShadow: 'var(--shadow-xl)', animation: 'scaleIn 0.2s ease',
          }} onClick={e => e.stopPropagation()}>
            
            <div style={{ textAlign: 'center', marginBottom: 'var(--space-xl)' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--accent-glow)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--space-md)' }}>
                <HelpCircle size={24} color="var(--accent-primary)" />
              </div>
              <h3 style={{ color: 'var(--text-primary)', fontSize: 17, fontWeight: 700, margin: '0 0 4px' }}>Help & Support</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: 0, lineHeight: 1.5 }}>
                We read every email. Typical response time: <strong style={{ color: 'var(--success)' }}>under 2 hours</strong> during business hours (Mon-Fri, 9am-6pm EST).
              </p>
            </div>

            {/* Email */}
            <a href="mailto:support@ileadx.io" style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
              borderRadius: 'var(--radius-md)', background: 'var(--bg-tertiary)',
              border: '1px solid var(--border-primary)', textDecoration: 'none',
              marginBottom: 'var(--space-lg)', transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.background = 'var(--accent-glow)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-primary)'; e.currentTarget.style.background = 'var(--bg-tertiary)'; }}>
              <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--info-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Mail size={20} color="var(--info)" />
              </div>
              <div>
                <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600 }}>Contact Support</span>
                <span style={{ display: 'block', color: 'var(--text-tertiary)', fontSize: 11 }}>Click to email us</span>
              </div>
            </a>

            {/* Quick help links */}
            <p style={{ color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Quick Help</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {[
                { icon: <BookOpen size={13} />, label: 'Getting Started Guide', onClick: () => { setShowHelpModal(false); navigate('/dashboard/new-search'); } },
                { icon: <Target size={13} />, label: 'How to Write Better Search Queries', onClick: () => { setShowHelpModal(false); navigate('/dashboard/new-search'); } },
                { icon: <Shield size={13} />, label: 'Understanding AI Scores & Pitch Angles', onClick: () => { setShowHelpModal(false); navigate('/dashboard/history'); } },
                { icon: <FileText size={13} />, label: 'Exporting Leads to CSV & Google Sheets', onClick: () => { setShowHelpModal(false); navigate('/dashboard/history'); } },
              ].map((item, i) => (
                <button key={i} onClick={item.onClick} style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '8px 12px', borderRadius: 'var(--radius-md)',
                  border: 'none', background: 'transparent', cursor: 'pointer',
                  color: 'var(--text-secondary)', fontSize: 12, textAlign: 'left',
                  transition: 'all 0.1s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-tertiary)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}>
                  {item.icon} {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Coming Soon Blink Animation ── */}
      <style>{`
        @keyframes comingSoonPulse {
          0%, 100% { 
            opacity: 1;
            box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.6);
          }
          50% { 
            opacity: 0.4;
            box-shadow: 0 0 0 8px rgba(245, 158, 11, 0);
          }
        }
        
        .coming-soon-tooltip {
          opacity: 0 !important;
        }
        button:hover + .coming-soon-tooltip {
          opacity: 1 !important;
        }
      `}</style>
    </>
  );
}

// ═══════════ DROPDOWN ITEM ═══════════
function DropdownItem({ icon, label, onClick, danger }) {
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
      padding: 'var(--space-sm) var(--space-md)', cursor: 'pointer',
      color: danger ? 'var(--error)' : 'var(--text-secondary)',
      fontSize: 13, borderBottom: '1px solid var(--border-primary)',
      transition: 'all 0.1s ease',
    }}
    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-tertiary)'; e.currentTarget.style.color = danger ? 'var(--error)' : 'var(--text-primary)'; e.currentTarget.style.borderLeft = '3px solid var(--accent-primary)'; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = danger ? 'var(--error)' : 'var(--text-secondary)'; e.currentTarget.style.borderLeft = '3px solid transparent'; }}>
      {icon}
      <span>{label}</span>
    </div>
  );
}

// ═══════════ SHARED STYLES ═══════════
const iconBtnStyle = {
  width: 42, height: 42, borderRadius: 'var(--radius-lg)',
  border: 'none', background: 'transparent', color: 'var(--text-secondary)',
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0, position: 'relative', transition: 'all 0.15s ease',
};
